/**
 * enfin levé — v57 reskin, stage 3: PDP behaviour.
 *
 * Deliberately minimal. Everything that Prestige already does is left to
 * Prestige (assets/theme.min.js):
 *   - variant selection            → ProductVariants, `change` on
 *                                    `.ProductForm__Option [type="radio"]`
 *   - price update                 → `.ProductMeta__PriceList` rewrite
 *   - add-to-cart + sold-out state → `.ProductForm__AddToCart` replacement
 *   - add to cart → cart drawer    → `[data-action="add-to-cart"]` +
 *                                    `product:added` event
 * This file only adds: the two native scroll-snap carousels, the accordions,
 * the CM/IN toggle and the MATERIALS jump. (The availability line is now
 * static, rendered server-side from a metafield — see section 5.)
 *
 * SCROLL POLICY (QA rounds 1+3+4)
 * -------------------------------
 * This file NEVER calls `preventDefault` on a vertical wheel. Exactly two
 * `wheel` listeners exist:
 *
 *   1. Vertical-wheel compensator — on `document`, CAPTURE + PASSIVE.
 *      Registered as the very first statement of the IIFE, before anything
 *      that could throw, so it is live even if the rest of the file fails.
 *      Being passive it physically cannot block scrolling; it only tops up a
 *      delta that the browser dropped. Rounds 1-3 kept this on the carousel
 *      viewport, which made it depend on `init()` + `nativeCarousel()` having
 *      run — QA proved the handler was never reached, so it now hangs off
 *      `document` and filters by `target.closest()` instead.
 *
 *   2. Shift+wheel horizontal fallback — on each carousel viewport,
 *      non-passive, but it returns before `preventDefault` unless the user is
 *      holding Shift over a track that can actually move horizontally.
 *
 * Nothing here scrolls the page on load or on `scrollend`.
 *
 * DIAGNOSTICS: `window.__elPdp` is populated immediately. Read it with
 * `JSON.stringify(window.__elPdp)`. If it is `undefined`, this file did not
 * execute at all (404 / blocked / cached old copy) — that is a delivery
 * problem, not a logic problem.
 *
 * Loaded with `defer` from sections/product-template.liquid.
 */
(function () {
  'use strict';

  /* ======================================================================
     0. Diagnostics — populated before anything can fail
     ====================================================================== */

  var DIAG = {
    version: 'qa7-loop',
    scriptRan: true,
    compensatorBound: false,
    section: false,
    galleryCol: false,
    galleryViewport: false,
    gallerySlides: 0,
    featuresViewport: false,
    wheelSeen: 0,
    wheelPassedGates: 0,
    wheelCompensated: 0,
    lastWheel: null,
    error: null
  };

  try { window.__elPdp = DIAG; } catch (e) { /* nothing we can do */ }

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  /* ======================================================================
     1. Vertical-wheel compensator  — REGISTERED FIRST, ON DOCUMENT
     ----------------------------------------------------------------------
     Why this exists: a horizontally scrollable box is unavoidably a scroll
     container in BOTH axes (CSS Overflow 3: with `overflow-x: auto`, a
     `clip`/`visible` on the other axis computes to `hidden`/`auto`). Some
     Chrome builds latch a vertical wheel onto such a box and drop the delta
     instead of chaining it to the page. The CSS answer is `overflow-y: auto`
     with zero vertical overflow; this is the safety net underneath it.

     It is PASSIVE — it can never block native scrolling or trackpad momentum.
     It waits a frame and only acts if nothing actually moved.
     ====================================================================== */

  function pageScroller() {
    return document.scrollingElement || document.documentElement;
  }

  /* Nearest ancestor that can really absorb `dy`, else null. */
  function scrollableAncestor(el, dy) {
    var node = el;
    while (node && node.nodeType === 1 && node !== document.body && node !== document.documentElement) {
      var overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        var room = node.scrollHeight - node.clientHeight;
        if (room > 1) {
          var atTop = node.scrollTop <= 0;
          var atBottom = node.scrollTop >= room - 1;
          if (!((dy < 0 && atTop) || (dy > 0 && atBottom))) return node;
        }
      }
      node = node.parentElement;
    }
    return null;
  }

  function onWheelCompensate(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('.gallery-viewport, .features-viewport')) return;

    DIAG.wheelSeen++;

    if (event.shiftKey) return;                                   // horizontal intent

    var unit = event.deltaMode === 1 ? 16 : (event.deltaMode === 2 ? window.innerHeight : 1);
    var dy = event.deltaY * unit;
    if (!dy) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return; // horizontal intent

    var scroller = pageScroller();
    var before = scroller.scrollTop;
    var room = scroller.scrollHeight - scroller.clientHeight;
    if (dy < 0 && before <= 0) return;                            // already at the top
    if (dy > 0 && before >= room - 1) return;                     // already at the bottom

    DIAG.wheelPassedGates++;
    DIAG.lastWheel = { dy: dy, before: before, room: room };

    window.requestAnimationFrame(function () {
      if (scroller.scrollTop !== before) return;                  // the browser handled it
      var node = scrollableAncestor(event.target, dy) || scroller;
      node.scrollTop += dy;
      DIAG.wheelCompensated++;
    });
  }

  try {
    document.addEventListener('wheel', onWheelCompensate, { passive: true, capture: true });
    DIAG.compensatorBound = true;
  } catch (e) {
    // very old browsers: options object unsupported -> capture boolean
    document.addEventListener('wheel', onWheelCompensate, true);
    DIAG.compensatorBound = true;
  }

  /* ======================================================================
     2. Native carousel (gallery + Selected Details)
     ----------------------------------------------------------------------
     No transformed track, no preventDefault on scroll: trackpad and touch
     stay 100% native (handover §8). JS adds mouse drag, Shift+wheel,
     arrows, keyboard and the counter.
     ====================================================================== */

  function nativeCarousel(root, opts) {
    if (!root) return null;

    var viewport = root.querySelector(opts.viewport);
    var slides = toArray(root.querySelectorAll(opts.slide));
    if (!viewport || slides.length === 0) return null;

    var prev = opts.prev ? root.querySelector(opts.prev) : null;
    var next = opts.next ? root.querySelector(opts.next) : null;
    var currentEl = opts.current ? root.querySelector(opts.current) : null;
    var totalEl = opts.total ? root.querySelector(opts.total) : null;

    var index = 0;
    var ticking = false;

    /* Infinite loop (client 24.09, main gallery only: opts.loop). JS adds one
       aria-hidden clone at each end: [last'] 1 2 ... n [first']. Whenever the
       viewport comes to rest on a clone it jumps, instantly and invisibly, to
       the identical real slide (normalize). Clones are made here, not in
       Liquid, so the first paint always shows the real first photo. */
    var realCount = slides.length;
    var loop = !!opts.loop && realCount > 1;

    if (loop) {
      var headClone = slides[realCount - 1].cloneNode(true);
      var tailClone = slides[0].cloneNode(true);
      [headClone, tailClone].forEach(function (clone) {
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', '');
        clone.removeAttribute('data-slide');
        var img = clone.querySelector('img');
        if (img) { img.setAttribute('alt', ''); img.removeAttribute('fetchpriority'); }
      });
      slides[0].parentNode.insertBefore(headClone, slides[0]);
      slides[0].parentNode.appendChild(tailClone);
      slides = [headClone].concat(slides, [tailClone]);
    }

    if (totalEl) totalEl.textContent = String(realCount);

    function slideLeft(i) {
      return slides[i].offsetLeft - slides[0].offsetLeft;
    }

    function nearest() {
      var x = viewport.scrollLeft;
      var best = 0;
      var bestDelta = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(slideLeft(i) - x);
        if (d < bestDelta) { bestDelta = d; best = i; }
      }
      return best;
    }

    function maxIndex() {
      // with multiple slides visible the last reachable index is < length - 1
      var limit = viewport.scrollWidth - viewport.clientWidth;
      for (var i = slides.length - 1; i >= 0; i--) {
        if (slideLeft(i) <= limit + 1) return i;
      }
      return 0;
    }

    // DOM index -> real photo index (clones map onto the photo they copy)
    function realIndex(i) {
      if (!loop) return i;
      if (i <= 0) return realCount - 1;
      if (i >= realCount + 1) return 0;
      return i - 1;
    }

    function sync() {
      index = nearest();
      if (currentEl) currentEl.textContent = String(realIndex(index) + 1);
      if (loop) return; // a loop has no first/last, arrows never disable
      var last = maxIndex();
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= last;
    }

    // set scrollLeft with no smooth-scroll animation, whatever the CSS says
    function jumpTo(left) {
      var inline = viewport.style.scrollBehavior;
      viewport.style.scrollBehavior = 'auto';
      viewport.scrollLeft = left;
      viewport.style.scrollBehavior = inline;
    }

    // on a clone -> the same offset on the real slide it copies
    function normalize() {
      if (!loop) return;
      var i = nearest();
      var shift = slideLeft(realCount + 1) - slideLeft(1);
      if (i === 0) jumpTo(viewport.scrollLeft + shift);
      else if (i === slides.length - 1) jumpTo(viewport.scrollLeft - shift);
    }

    /* Touch-only mode (client 24.09, see the touch block below). Phones and
       tablets without a mouse/trackpad only: `(hover: none) and
       (pointer: coarse)`. Desktop, laptops and an iPad with a trackpad keep
       the native scroller exactly as before. */
    var touchMode = !!(opts.touchSwipe && window.matchMedia &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches);
    var anim = 0;
    var touch = null;

    function stopAnim() {
      if (anim) { window.cancelAnimationFrame(anim); anim = 0; }
    }

    // JS easing on scrollLeft; the viewport is overflow:hidden in touch mode,
    // so there is no native scroll animation for iOS to latch a touch onto
    function animateTo(left, instant) {
      stopAnim();
      var from = viewport.scrollLeft;
      var dist = left - from;
      if (instant || Math.abs(dist) < 1) { viewport.scrollLeft = left; normalize(); return; }
      var t0 = null;
      var step = function (now) {
        if (t0 === null) t0 = now;
        var p = Math.min(1, (now - t0) / 280);
        viewport.scrollLeft = from + dist * (1 - Math.pow(1 - p, 3));
        anim = p < 1 ? window.requestAnimationFrame(step) : 0;
        if (!anim) normalize();
      };
      anim = window.requestAnimationFrame(step);
    }

    function go(i, behavior) {
      var target = Math.max(0, Math.min(i, slides.length - 1));
      if (touchMode) {
        animateTo(slideLeft(target), behavior === 'auto' || REDUCED);
        return;
      }
      viewport.scrollTo({
        left: slideLeft(target),
        behavior: behavior || (REDUCED ? 'auto' : 'smooth')
      });
    }

    /* Loop: once scrolling has stopped (trackpad momentum, snap, smooth
       scroll, arrows) and no drag or animation is in progress, leave the
       clone. scrollend where supported, a 150ms idle timer everywhere. */
    var idleTimer = 0;
    function onIdle() {
      window.clearTimeout(idleTimer);
      if (dragging || anim || (touch && touch.active)) return;
      normalize();
    }

    viewport.addEventListener('scroll', function () {
      if (loop) {
        window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(onIdle, 150);
      }
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        sync();
      });
    }, { passive: true });

    viewport.addEventListener('scrollend', sync);
    if (loop) viewport.addEventListener('scrollend', onIdle);

    if (prev) prev.addEventListener('click', function () { go(nearest() - 1); });
    if (next) next.addEventListener('click', function () { go(nearest() + 1); });

    if (opts.keyboard) {
      root.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') { event.preventDefault(); go(nearest() - 1); }
        else if (event.key === 'ArrowRight') { event.preventDefault(); go(nearest() + 1); }
      });
    }

    /* Mouse drag-to-scroll. Pointer events only, mouse only, left button only —
       touch and trackpad never reach this path. */
    var dragging = false;
    var startX = 0;
    var startScroll = 0;
    var moved = false;

    viewport.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
    });

    viewport.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      var delta = event.clientX - startX;
      if (Math.abs(delta) > 3) moved = true;
      viewport.scrollLeft = startScroll - delta;
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      if (moved) go(nearest());
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', endDrag);

    /* Touch swipe (client 24.09, mobile gallery). On iOS a native horizontal
       scroller catches every new touch while it is still moving (momentum,
       snap or smooth scroll), so right after flicking a photo a swipe down
       did nothing until the gallery had settled. In touch mode the viewport
       is therefore NOT a user scroller at all: `.is-touch-swipe` sets
       `overflow: hidden; touch-action: pan-y pinch-zoom`. Vertical page
       scroll and pinch zoom stay 100% native; horizontal moves reach us as
       pointer events, follow the finger and page one slide (animateTo). A new
       touch stops any running animation, so nothing is ever left in motion.
       Vertical-dominant gestures are released untouched. The class is added
       here, so without JS the native scroller stays as it was. */
    if (touchMode) {
      viewport.classList.add('is-touch-swipe');

      viewport.addEventListener('pointerdown', function (event) {
        if (event.pointerType !== 'touch') return;
        if (!event.isPrimary) { touch = null; return; } // second finger = pinch
        stopAnim();
        normalize(); // a touch mid-animation may catch the gallery on a clone
        touch = {
          x: event.clientX,
          y: event.clientY,
          scroll: viewport.scrollLeft,
          from: nearest(),
          t: Date.now(),
          dx: 0,
          active: false
        };
      });

      viewport.addEventListener('pointermove', function (event) {
        if (!touch || event.pointerType !== 'touch' || !event.isPrimary) return;
        var dx = event.clientX - touch.x;
        var dy = event.clientY - touch.y;
        if (!touch.active) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          if (Math.abs(dy) >= Math.abs(dx)) { touch = null; go(nearest()); return; } // page scroll wins
          touch.active = true;
        }
        touch.dx = dx;
        viewport.scrollLeft = touch.scroll - dx;
      });

      var endTouch = function (event) {
        if (!touch || event.pointerType !== 'touch') return;
        var t = touch;
        touch = null;
        if (!t.active) { go(nearest()); return; } // a tap that stopped an animation
        var fast = Math.abs(t.dx) / Math.max(1, Date.now() - t.t) > 0.3;
        var target = t.from;
        if (Math.abs(t.dx) > viewport.clientWidth * 0.15 || (fast && Math.abs(t.dx) > 20)) {
          target = t.from + (t.dx < 0 ? 1 : -1);
        }
        go(target);
      };

      viewport.addEventListener('pointerup', endTouch);
      viewport.addEventListener('pointercancel', endTouch);
    }

    /* Shift+wheel fallback for classic mice — the ONLY preventDefault on a
       wheel in this file, and it is unreachable without Shift. */
    viewport.addEventListener('wheel', function (event) {
      // 1. plain / vertical wheel -> hands off, let the page scroll
      if (!event.shiftKey) return;
      // 2. the browser already reports a horizontal component -> it handles it
      if (Math.abs(event.deltaX) >= 1) return;
      // 3. nothing to scroll horizontally -> hands off
      var max = viewport.scrollWidth - viewport.clientWidth;
      if (max <= 1) return;
      // 4. already at the edge we are being pushed towards -> hands off
      var delta = event.deltaY;
      if (delta === 0) return;
      if (delta < 0 && viewport.scrollLeft <= 0) return;
      if (delta > 0 && viewport.scrollLeft >= max - 1) return;

      event.preventDefault();
      viewport.scrollLeft += delta;
    }, { passive: false });

    window.addEventListener('resize', function () {
      viewport.scrollTo({ left: slideLeft(Math.min(index, slides.length - 1)), behavior: 'auto' });
      sync();
    });

    if (loop) jumpTo(slideLeft(1)); // start on the real first photo, same task as the clone insert
    sync();
    return { go: go, sync: sync };
  }

  /* ======================================================================
     3. Accordions
     ====================================================================== */

  function initAccordions(scope) {
    var accordions = toArray(scope.querySelectorAll('.acc'));
    var triggers = accordions
      .map(function (acc) { return acc.querySelector(':scope > button'); })
      .filter(Boolean);

    function setState(acc, open) {
      var button = acc.querySelector(':scope > button');
      var state = acc.querySelector('.acc-state');
      acc.classList.toggle('open', open);
      if (button) button.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (state) state.textContent = open ? 'Close' : '+';
    }

    triggers.forEach(function (button) {
      var acc = button.closest('.acc');
      setState(acc, acc.classList.contains('open'));

      button.addEventListener('click', function () {
        var willOpen = !acc.classList.contains('open');
        // one open at a time
        accordions.forEach(function (other) {
          if (other !== acc && other.classList.contains('open')) setState(other, false);
        });
        setState(acc, willOpen);
      });
    });

    // `[data-size-guide]` anywhere on the page opens the sizing accordion.
    // Click-only: nothing here runs on page load.
    toArray(document.querySelectorAll('[data-size-guide]')).forEach(function (link) {
      link.addEventListener('click', function (event) {
        var acc = scope.querySelector('.sizing-acc');
        if (!acc) return;
        event.preventDefault();
        accordions.forEach(function (other) {
          if (other !== acc && other.classList.contains('open')) setState(other, false);
        });
        setState(acc, true);
        acc.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'nearest' });
      });
    });
  }

  /* ======================================================================
     4. CM / IN toggle — two complete tables, swapped by [hidden]
     ====================================================================== */

  function initUnitToggle(scope) {
    toArray(scope.querySelectorAll('.sizing-content')).forEach(function (content) {
      var buttons = toArray(content.querySelectorAll('.unit-btn'));
      var tables = toArray(content.querySelectorAll('[data-unit-table]'));

      buttons.forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation(); // otherwise the click would close the accordion
          var unit = button.getAttribute('data-unit');
          buttons.forEach(function (other) {
            var active = other === button;
            other.classList.toggle('active', active);
            other.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          tables.forEach(function (table) {
            table.hidden = table.getAttribute('data-unit-table') !== unit;
          });
        });
      });
    });
  }

  /* ======================================================================
     5. Availability line — REMOVED (client request VOL 1 §7)

     The line used to mirror the selected variant's stock level, rewriting
     itself on `variant:changed` from data-text-in-stock / -low / -sold-out.
     The client now drives it manually from the product metafield
     `custom.availability_status`, so it is identical for every variant and is
     rendered once, server-side, by sections/product-template.liquid. The
     data-text-* attributes were dropped from the Liquid along with this
     handler.

     Nothing else changes: the variant machinery (section 3), the gallery
     (section 2) and add-to-cart still behave exactly as before — they simply
     no longer have a listener on this element.
     ====================================================================== */

  /* ======================================================================
     6. MATERIALS ↓ smooth scroll
     ----------------------------------------------------------------------
     Replaces the global `html { scroll-behavior: smooth }` that shipped in
     the first build. That rule made EVERY programmatic scroll on the page
     asynchronous, which is a page-wide side effect we do not want. This is
     scoped to the one link, and it is the only scroll this file initiates.
     The header offset still comes from CSS `scroll-margin-top`.
     ====================================================================== */

  function initMaterialJump(section) {
    toArray(section.querySelectorAll('.material-jump-link')).forEach(function (link) {
      link.addEventListener('click', function (event) {
        var href = link.getAttribute('href') || '';
        if (href.charAt(0) !== '#') return;
        var target = document.getElementById(href.slice(1));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  /* ====================================================================== */

  function init() {
    var section = document.querySelector('.ElPdpSection');
    if (!section) return;
    DIAG.section = true;

    var galleryCol = section.querySelector('.gallery-col');
    DIAG.galleryCol = !!galleryCol;
    if (galleryCol) {
      DIAG.galleryViewport = !!galleryCol.querySelector('.gallery-viewport');
      DIAG.gallerySlides = galleryCol.querySelectorAll('.gallery-slide').length;
    }

    // root is `.gallery-col` (gallery stage + meta bar), see product-template.liquid
    nativeCarousel(galleryCol, {
      viewport: '.gallery-viewport',
      slide: '.gallery-slide',
      prev: '.gallery-arrow.prev',
      next: '.gallery-arrow.next',
      current: '.gallery-current',
      total: '.gallery-total',
      keyboard: true,
      touchSwipe: true,
      loop: true
    });

    var features = section.querySelector('.features-carousel');
    DIAG.featuresViewport = !!(features && features.querySelector('.features-viewport'));

    nativeCarousel(features, {
      viewport: '.features-viewport',
      slide: '.feature-slide',
      prev: '.feature-arrow.prev',
      next: '.feature-arrow.next',
      current: '.features-current',
      total: '.features-total',
      keyboard: false
    });

    initAccordions(section);
    initUnitToggle(section);
    initMaterialJump(section);
  }

  function safeInit() {
    try {
      init();
    } catch (error) {
      // never let a failure here take the wheel compensator down with it
      DIAG.error = (error && error.message) ? error.message : String(error);
      if (window.console && window.console.error) {
        window.console.error('[enfin-v57-pdp] init failed:', error);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }
})();
